/** Stage E3.1 — Market Memory panel (TanStack Query — LONG stale)
 * Shows a symbol's verdict evolution over time.
 */
import { Loader2, ArrowRight } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { keys } from "@/lib/queryKeys";
import { STALE, GC } from "@/lib/queryClient";
import { formatLocalDateISO } from "@/lib/dates";

function VerdictPill({ v, muted = false }) {
  if (!v) return <span className="mono text-[10px] text-muted-foreground">—</span>;
  const cls = /BUY/.test(v) ? "pnl-up" : /SELL|REDUCE/.test(v) ? "pnl-down" :
              /WATCH|HOLD/.test(v) ? "text-signal-yellow" : "";
  return <span className={`chip !text-[10px] border-current ${muted ? "text-muted-foreground line-through" : cls}`}>{v.replace(/_/g, " ")}</span>;
}

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  if (diff < 60_000) return "just now";
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
  const days = Math.floor(diff / 86_400_000);
  if (days < 30) return `${days}d ago`;
  return d.toLocaleDateString();
}

export default function MarketMemoryPanel({ symbol }) {
  const { data, isLoading: loading, isError, refetch } = useQuery({
    queryKey: keys.marketMemory(symbol),
    queryFn: async () => (await api.get(`/market-intel/memory/${symbol}`)).data,
    enabled: !!symbol,
    staleTime: STALE.LONG,
    gcTime: GC.LONG,
  });

  if (loading) return <div className="h-24 flex items-center text-xs text-muted-foreground"><Loader2 size={12} className="animate-spin mr-2"/>Loading memory…</div>;
  if (isError) return (
    <div className="border border-dashed border-signal-red/40 p-4 text-center text-xs text-muted-foreground" role="alert">
      Failed to load memory. <button onClick={() => refetch()} className="underline underline-offset-2 hover:text-foreground focus:outline-none focus:ring-1 focus:ring-current" data-testid="memory-retry">Retry</button>
    </div>
  );
  const entries = data?.entries || [];
  if (entries.length === 0) return (
    <div className="border border-dashed border-border/40 p-4 text-center text-xs text-muted-foreground" data-testid="memory-empty">
      No verdict evolution recorded yet for {symbol}. Every new analysis will be logged here.
    </div>
  );

  return (
    <div className="space-y-2" data-testid="market-memory-panel">
      <div className="flex items-center gap-2 mb-1">
        <div className="label-cap">Market Memory · {symbol}</div>
        <span className="ml-auto mono text-[10px] text-muted-foreground">{entries.length} events</span>
        {data?.current_verdict && (
          <>
            <span className="mono text-[10px] text-muted-foreground">current:</span>
            <VerdictPill v={data.current_verdict}/>
          </>
        )}
      </div>
      <ol className="space-y-2">
        {entries.map((e, i) => (
          <li key={e.id || i} className="border border-border/40 p-2" data-testid={`memory-event-${i}`}>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="mono text-[10px] text-muted-foreground" title={e.at}>{formatLocalDateISO(e.at)}</span>
              <VerdictPill v={e.prev_verdict} muted/>
              {e.prev_verdict && <ArrowRight size={11} className="text-muted-foreground"/>}
              <VerdictPill v={e.new_verdict}/>
              {e.confidence != null && <span className="mono text-[10px] text-muted-foreground">conf {e.confidence}</span>}
              {e.risk_score != null && <span className="mono text-[10px] text-muted-foreground">risk {e.risk_score}</span>}
              <span className="ml-auto mono text-[9px] text-muted-foreground">{timeAgo(e.at)}</span>
            </div>
            {(e.reason_labels || []).length > 0 && (
              <ul className="mt-1 space-y-0.5 text-[11px] text-foreground/85">
                {e.reason_labels.map((r, idx) => (
                  <li key={idx} className="pl-3 relative before:content-['·'] before:absolute before:left-0">{r}</li>
                ))}
              </ul>
            )}
            {e.refresh_count && e.refresh_count > 1 && (
              <div className="mt-1 mono text-[10px] text-muted-foreground">Confirmed by {e.refresh_count} refreshes</div>
            )}
          </li>
        ))}
      </ol>
    </div>
  );
}
